/**
 * D266: the idle game's first run. One card: the world's name, already filled with a
 * suggestion from the data, the seed that shapes its land (D267), and Continue, which makes
 * the idle world and opens it at the first tip. D344: a sketch of the land the seed gives, and
 * its kind and traits, show under the seed and change with each Shuffle. "Sign in" opens the sign-in
 * sheet, and a sign-in that finds a world opens it instead. A signed-in player whose worlds
 * are all archived comes here too (D267). D337: settings' "Start a new world" opens it over the
 * world being played, with Cancel in place of signing in; the worlds already there stay as they
 * are, and the new one opens. D359: the card opens with the theme picker, Farm or Compute,
 * each with a small picture of a built world, its name and a line about it; the name's
 * suggestions, the words and the land sketch follow the theme chosen. The theme is sent once,
 * with the world, and is fixed from then on (D350). D453: only open themes are offered, and with
 * one (the farm) the picker goes. D414: a new player on a guest account sees
 * one step before all that, "Keep your worlds safe": a passkey, an email, or "Play as a guest".
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { getIdleContent, openThemeIds, type IdleContent } from '@hedgerow/data';
import { isMapSeed, normaliseMapSeed, randomMapSeed } from '@hedgerow/sim';
import { now } from '../clock.ts';
import { REASON_NEEDS_WORLD_NAME, REASON_WAIT } from '../game/copy.ts';
import { openSignIn } from '../game/signin-bus.ts';
import { currentAccount, registerPasskey, type Account } from '../game/sync.ts';
import { Button } from '../ui/Button.tsx';
import { IsoWorldMap } from '../ui/IsoWorldMap.tsx';
import { LandSketch } from './LandSketch.tsx';
import { landingScene } from './landing-world.ts';
import { idleContent, useIdle } from './store.ts';
import { ThemeSwatch } from './ThemeSwatch.tsx';

/** A world name from the data, drawn at random, never the one already showing. */
export function suggestWorldName(names: readonly string[], not?: string): string {
  const pool = names.length > 1 ? names.filter((n) => n !== not) : names;
  return pool[Math.floor(Math.random() * pool.length)] ?? names[0] ?? '';
}

/** An accent text link in a 44px target (Suggest another, Shuffle). */
const LINK_BUTTON = { height: 44, padding: '0 8px', color: 'var(--accent)' } as const;

const LINE = {
  margin: 0,
  font: '400 var(--fs-name)/1.4 var(--font-ui)',
  color: 'var(--ink-2)',
} as const;

/** "three" for 3: the limit as the line under the button says it. */
const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const numberWord = (n: number): string => WORDS[n] ?? String(n);

/** The seed every theme's picture is drawn from, so the picker looks the same each time. */
const PREVIEW_SEED = 7;

/** D359: a small still picture of a built world in one theme. */
function ThemePreview({ theme, height = 88 }: { theme: string; height?: number }) {
  const scene = useMemo(() => landingScene(theme, PREVIEW_SEED, now()), [theme]);
  return (
    <div aria-hidden="true" style={{ position: 'relative', height, pointerEvents: 'none' }}>
      <IsoWorldMap
        still
        theme={scene.theme}
        parcels={scene.parcels}
        buildings={scene.buildings}
        plot={scene.plot}
        worldPlot={scene.worldPlot}
        lands={scene.lands}
        country={scene.country}
        stream={scene.stream}
        showSigns={false}
        decor={scene.decor}
        style={{ position: 'absolute', inset: 0, height: '100%', aspectRatio: 'auto' }}
      />
    </div>
  );
}

/** D359: the theme picker: a card for each theme, the chosen one marked. */
function ThemePicker({ value, onPick }: { value: string; onPick: (id: string) => void }) {
  return (
    <div
      role="radiogroup"
      aria-label="World"
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}
    >
      {openThemeIds().map((id) => {
        const name = getIdleContent(id).theme.name;
        const on = id === value;
        const dark = document.documentElement.getAttribute('data-theme') === 'dark';
        return (
          <button
            key={id}
            type="button"
            // The card takes its own theme's accent for its outline (D431 step 5).
            data-world={id}
            {...(dark ? { 'data-theme': 'dark' } : {})}
            role="radio"
            aria-checked={on}
            aria-label={name}
            data-testid={`theme-${id}`}
            onClick={() => onPick(id)}
            style={{
              display: 'grid',
              alignContent: 'start',
              justifyItems: 'start',
              gap: 8,
              padding: 12,
              textAlign: 'left',
              border: `2px solid ${on ? 'var(--accent)' : 'var(--line)'}`,
              borderRadius: 12,
              background: 'var(--surface)',
              color: 'var(--ink)',
              cursor: 'pointer',
              minHeight: 44,
              minWidth: 0,
            }}
          >
            <ThemeSwatch theme={id} size={32} icon={20} />
            <span style={{ display: 'grid', gap: 2, minWidth: 0, width: '100%' }}>
              <span style={{ font: '600 var(--fs-body)/1.3 var(--font-ui)' }}>{name}</span>
              <span style={{ ...LINE, fontSize: 12, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                {getIdleContent(id).theme.description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function FirstRun() {
  const needs = useIdle((s) => s.firstRun);
  const another = useIdle((s) => s.newWorldOpen);
  const worlds = useIdle((s) => s.worlds);
  // D359: the theme the world will be made in; its words, names and land from here on.
  const [theme, setTheme] = useState(() => openThemeIds()[0] ?? 'farm');
  const content = getIdleContent(theme);
  const many = openThemeIds().length > 1;
  // D337: a suggestion is never the name of a world the player already plays.
  const mine = new Set(worlds.filter((f) => !f.archived).map((f) => f.name.trim().toLowerCase()));
  const poolOf = (c: IdleContent) => {
    const names = c.worldNames.filter((n) => !mine.has(n.toLowerCase()));
    return names.length ? names : c.worldNames;
  };
  const pool = poolOf(content);
  const [name, setName] = useState(() => suggestWorldName(pool));
  const [seed, setSeed] = useState(() => randomMapSeed(String(Math.random())));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  // D414: the card holds blank until the account is known, so the picker never flashes.
  const [account, setAccount] = useState<Account | null>(null);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    let live = true;
    void currentAccount()
      .then((a) => live && setAccount(a))
      .catch(() => undefined)
      .finally(() => live && setChecked(true));
    return () => {
      live = false;
    };
  }, []);
  const [accountDone, setAccountDone] = useState(false);
  const [passkeyNote, setPasskeyNote] = useState<string | null>(null);
  const [keying, setKeying] = useState(false);
  const trimmed = name.trim();
  const clash = mine.has(trimmed.toLowerCase());
  const seedOk = isMapSeed(normaliseMapSeed(seed));

  // A name the player has not typed follows the theme: a suggestion from its own list.
  const pickTheme = (id: string) => {
    if (id === theme) return;
    if (!trimmed || content.worldNames.includes(trimmed))
      setName(suggestWorldName(poolOf(getIdleContent(id))));
    setTheme(id);
  };

  const start = async () => {
    if (!trimmed || !seedOk || clash || busy) return;
    setBusy(true);
    setFailed(null);
    const r = await useIdle.getState().createWorld(trimmed, normaliseMapSeed(seed), theme);
    setBusy(false);
    if (r === 'failed') setFailed('Could not make the world. Try again.');
    else if (typeof r === 'object') setFailed(r.reason);
    if (r === 'sign_in') openSignIn();
  };

  const createPasskey = async () => {
    setKeying(true);
    setPasskeyNote(null);
    const r = await registerPasskey();
    setKeying(false);
    if (r.ok) setAccountDone(true);
    else setPasskeyNote(r.message);
  };

  // D414: only a new player on a guest account is asked; a returning player, a player who is
  // already signed in and a world started from settings go straight to the picker.
  const askAccount = !accountDone && !another && needs !== 'sign_in' && account?.isGuest === true;

  return (
    <>
      <div
        data-testid="first-run"
        data-screen-label="First run"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--bg)',
          overflowY: 'auto',
          zIndex: 20,
        }}
      >
        {!checked ? null : askAccount ? (
          <div
            data-testid="first-run-account"
            style={{
              width: '100%',
              maxWidth: 440,
              display: 'grid',
              gap: 16,
              boxSizing: 'border-box',
              margin: '0 auto',
              padding: 24,
            }}
          >
            <h1 style={{ margin: 0, font: '600 var(--fs-heading)/1.2 var(--font-ui)' }}>
              Keep your worlds safe
            </h1>
            <p style={LINE}>
              A passkey or an email keeps your worlds if you clear this browser or change device.
            </p>
            {passkeyNote && (
              <p style={{ ...LINE, fontSize: 14, color: 'var(--red)' }} role="alert">
                {passkeyNote}
              </p>
            )}
            <Button
              variant="primary"
              full
              disabled={keying}
              explain={REASON_WAIT}
              onClick={() => void createPasskey()}
            >
              Create a passkey
            </Button>
            <Button variant="secondary" full onClick={openSignIn}>
              Use email
            </Button>
            <Button variant="ghost" full onClick={() => setAccountDone(true)}>
              Play as a guest
            </Button>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void start();
            }}
            style={{
              width: '100%',
              maxWidth: 440,
              display: 'grid',
              gap: 16,
              boxSizing: 'border-box',
              margin: '0 auto',
              padding: '0 24px',
              minHeight: '100%',
              alignContent: 'start',
            }}
          >
            {/* D439 step 6: no header or tabs; the chosen theme's world across the top 300px,
                a still picture. */}
            <div style={{ height: 300, margin: '0 -24px', overflow: 'hidden' }}>
              <ThemePreview theme={theme} height={300} />
            </div>
            <h1 style={{ margin: 0, font: '700 var(--fs-heading)/1.2 var(--font-ui)' }}>
              {another ? 'Start a new world' : 'Start your first world'}
            </h1>
            {/* D453: with one theme open there is nothing to pick, so the card names the farm. */}
            <p style={LINE}>{many ? 'Choose a world. ' : ''}It keeps earning while you are away.</p>
            {many && <ThemePicker value={theme} onPick={pickTheme} />}
            <span
              style={{ font: '600 var(--fs-caption)/1.2 var(--font-ui)', color: 'var(--ink-2)' }}
            >
              Name your world
            </span>
            <input
              value={name}
              onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
              aria-label="World name"
              autoComplete="off"
              maxLength={40}
              style={{
                height: 44,
                padding: '0 12px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--surface)',
                color: 'var(--ink)',
                font: '400 var(--fs-name)/1.4 var(--font-ui)',
                outline: 'none',
              }}
            />
            {clash && (
              <p style={{ ...LINE, fontSize: 14 }} role="alert">
                You already have a world with that name.
              </p>
            )}
            <Button
              variant="ghost"
              style={{ ...LINK_BUTTON, justifySelf: 'start' }}
              onClick={() => setName(suggestWorldName(pool, trimmed))}
            >
              Suggest another
            </Button>
            <label style={{ display: 'grid', gap: 6 }}>
              <span
                style={{ font: '600 var(--fs-caption)/1.2 var(--font-ui)', color: 'var(--ink-2)' }}
              >
                Seed
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={seed}
                  data-testid="first-run-seed"
                  onInput={(e) => setSeed((e.currentTarget as HTMLInputElement).value)}
                  aria-label="Seed"
                  autoComplete="off"
                  spellcheck={false}
                  maxLength={32}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: 44,
                    padding: '0 12px',
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    font: '400 var(--fs-name)/1.4 var(--font-num)',
                    outline: 'none',
                  }}
                />
                <Button
                  variant="ghost"
                  style={LINK_BUTTON}
                  onClick={() => setSeed(randomMapSeed(String(Math.random())))}
                >
                  Shuffle
                </Button>
              </div>
            </label>
            {/* D344: the land this seed gives, before the world is made; D359: in its theme. */}
            {seedOk && <LandSketch seed={seed} theme={theme} />}
            {!seedOk && (
              <p style={{ ...LINE, fontSize: 14 }} role="alert">
                A seed is letters, numbers, spaces and dashes.
              </p>
            )}
            {needs === 'sign_in' && (
              <p style={LINE} data-testid="first-run-sign-in">
                This device has signed in before. Sign in to open your world.
              </p>
            )}
            {failed && (
              <p style={{ ...LINE, color: 'var(--red)' }} role="alert">
                {failed}
              </p>
            )}
            <p style={{ ...LINE, fontSize: 13, textAlign: 'center' }}>
              You can keep up to {numberWord(idleContent.worldsPerPlayer)} worlds.
            </p>
            {/* Pinned to the foot of the screen, above the safe-area inset, so it never scrolls away. */}
            <div
              data-testid="first-run-actions"
              style={{
                position: 'sticky',
                bottom: 0,
                display: 'flex',
                flexDirection: 'row-reverse',
                gap: 8,
                margin: 'auto -24px 0',
                padding: '12px 24px calc(12px + env(safe-area-inset-bottom))',
                background: 'var(--bg)',
                borderTop: '1px solid var(--line)',
              }}
            >
              <Button
                variant="primary"
                style={{ flex: 1 }}
                disabled={!trimmed || !seedOk || clash || busy}
                explain={
                  busy
                    ? REASON_WAIT
                    : !trimmed
                      ? REASON_NEEDS_WORLD_NAME
                      : clash
                        ? 'Choose another name'
                        : 'Needs a seed'
                }
                onClick={() => void start()}
              >
                {many ? `Continue with ${content.theme.name}` : 'Continue'}
              </Button>
              {another ? (
                <Button variant="ghost" onClick={() => useIdle.getState().setNewWorldOpen(false)}>
                  Cancel
                </Button>
              ) : (
                <Button variant="ghost" onClick={openSignIn}>
                  Sign in
                </Button>
              )}
            </div>
          </form>
        )}
      </div>
    </>
  );
}
